<template>
  <div class="fillHorizontal displayContents" :data="modelValue">
    <nav v-if="hasToolbar" class="level toolbar">
      <!-- Left side -->
      <div v-if="isMounted" class="level-left">
        <div class="level-item">
          <el-select v-model="view" placeholder="Select view" @change="changeView">
            <template #prefix>
              <font-awesome-icon :icon="['fac', view]" />
            </template>
            <el-option v-for="item in Object.values(Views)" :key="item" :value="item" :label="$t(`enum.views.${item}`)" :disabled="item === Views.custom">
              <font-awesome-icon :icon="['fac', item]" />
              {{ $t(`enum.views.${item}`) }}
            </el-option>
          </el-select>
        </div>
        <div class="level-item action" @click="zoom(0.1)">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.zoomIn')" placement="bottom">
            <font-awesome-icon icon="search-plus" />
          </el-tooltip>
        </div>
        <div class="level-item action" @click="zoom(-0.1)">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.zoomOut')" placement="bottom">
            <font-awesome-icon icon="search-minus" />
          </el-tooltip>
        </div>
        <div class="level-item action" @click="fitCameraToSelection">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.zoomFit')" placement="bottom">
            <font-awesome-icon icon="video" />
          </el-tooltip>
        </div>
        <div class="level-item action" :class="{ active: displayGrid }" @click="toggleGrid">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.grid')" placement="bottom">
            <font-awesome-icon icon="border-none" />
          </el-tooltip>
        </div>
        <div class="level-item action" :class="{ active: displayVertexNormals }" @click="toggleVertexNormals">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.vertexNormals')" placement="bottom">
            <font-awesome-icon icon="arrow-up-from-bracket" />
          </el-tooltip>
        </div>
        <div class="level-item action" :class="{ active: displayBoundingBox }" @click="toggleBoundingBox">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.boundingBox')" placement="bottom">
            <font-awesome-icon :icon="['fac', 'custom']" />
          </el-tooltip>
        </div>
        <div v-if="canToggleBones && boneTools" class="level-item action" :class="{ active: boneTools.displayBones }" @click="toggleBones">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.bones')" placement="bottom">
            <font-awesome-icon icon="bone" />
          </el-tooltip>
        </div>
      </div>

      <!-- Right side -->
      <div v-if="isMounted" class="level-right">
        <div v-if="canUndo" class="level-item action" :class="{ disabled: !modelValue.historyList.hasUndo }" @click="undo">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.undo')" placement="bottom">
            <font-awesome-icon icon="rotate-left" />
          </el-tooltip>
        </div>
        <div v-if="canUndo" class="level-item action" :class="{ disabled: !modelValue.historyList.hasRedo }" @click="redo">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.redo')" placement="bottom">
            <font-awesome-icon icon="rotate-right" />
          </el-tooltip>
        </div>
        <RenderTools
          ref="renderTools"
          v-model="modelValue"
          :trois-renderer="troisRenderer"
          :editor-mode="editorMode"
          :selection-list="selectionList"
          :view-ctrl="viewCtrl"
          :select-object="selectObject"
          :can-render="canRender"
          :history-list="modelValue.historyList"
        />
        <ProjectionTools
          v-if="editorMode.isDefaultMode"
          ref="projectionTools"
          v-model="modelValue"
          :trois-renderer="troisRenderer"
          :editor-mode="editorMode"
          :selection-list="selectionList"
          :has-selection="hasSelection"
          :view-ctrl="viewCtrl"
          :select-object="selectObject"
          :can-project="canEmbossing"
          :embossing="selectedEmbossing"
          :history-list="modelValue.historyList"
        />
        <CurveTools
          ref="curveTools"
          v-model="modelValue"
          :trois-renderer="troisRenderer"
          :editor-mode="editorMode"
          :selection-list="selectionList"
          :has-selection="hasSelection"
          :view-ctrl="viewCtrl"
          :can-curve="canCurve"
          :select-object="selectObject"
          :allowed-curve-categories="allowedCurveCategories"
          :history-list="modelValue.historyList"
          :can-calculate-result="canCalculateResult"
        />
        <DeformationTools
          ref="deformationTools"
          v-model="modelValue"
          v-model:loading-circle="loadingCircle"
          :can-deform="canDeform"
          :trois-renderer="troisRenderer"
          :selection-list="selectionList"
          :has-selection="hasSelection"
          :history-list="modelValue.historyList"
          :select-mesh-by-id="selectMesh"
        />
        <el-popover
          v-if="editorMode.isDefaultMode && canColorize"
          placement="bottom-start"
          :title="$t('components.mesh-editor.colors')"
          :width="200"
          trigger="click"
          :disabled="!hasSelection"
        >
          <template #reference>
            <div class="level-item action" :class="{ disabled: !hasSelection }">
              <el-tooltip :content="$t('components.mesh-editor.tooltip.color')" placement="bottom">
                <font-awesome-icon icon="palette" />
              </el-tooltip>
            </div>
          </template>
          <el-space wrap>
            <div v-for="color in colors" :key="color" class="action" @click="colorizeSelected(color)">
              <font-awesome-icon icon="square" :style="{ color: color }" />
            </div>
          </el-space>
        </el-popover>
        <el-popover
          v-if="editorMode.isDefaultMode && canTransform"
          ref="popoverName"
          placement="bottom-start"
          :title="$t('components.mesh-editor.name')"
          :width="200"
          trigger="click"
          :disabled="!hasSelection"
        >
          <template #reference>
            <div class="level-item action" :class="{ disabled: !hasSelection }">
              <el-tooltip :content="$t('components.mesh-editor.tooltip.objectName')" placement="bottom">
                <font-awesome-icon icon="heading" />
              </el-tooltip>
            </div>
          </template>
          <el-form>
            <el-form-item>
              <el-input v-model="selectedName" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="saveName">
                {{ $t('components.mesh-editor.commit') }}
              </el-button>
            </el-form-item>
          </el-form>
        </el-popover>
        <div v-if="editorMode.isDefaultMode && canGroup" class="level-item action" :class="{ disabled: !hasSelection }" @click="groupObject">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.group')" placement="bottom">
            <font-awesome-icon icon="object-group" />
          </el-tooltip>
        </div>
        <div v-if="editorMode.isDefaultMode && canAline" class="level-item action" :class="{ disabled: !hasSelection }" @click="alineSelectedToFloor">
          <el-tooltip :content="$t('components.mesh-editor.tooltip.align')" placement="bottom">
            <font-awesome-icon :icon="['fac', 'objects_align_bottom']" />
          </el-tooltip>
        </div>
        <div v-if="editorMode.isDefaultMode && canCopy" class="level-item">
          <el-dropdown class="level-item">
            <div :class="{ disabled: !hasSelection }">
              <el-tooltip :content="$t('components.mesh-editor.tooltip.clone')" placement="left">
                <font-awesome-icon icon="clone" />
              </el-tooltip>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item class="action" @click="copySelected(AxisType.x)"> x </el-dropdown-item>
                <el-dropdown-item class="action" @click="copySelected(AxisType.y)"> y </el-dropdown-item>
                <el-dropdown-item class="action" @click="copySelected(AxisType.z)"> z </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
        <div v-if="editorMode.isDefaultMode && canMirror" class="level-item">
          <el-dropdown class="level-item">
            <div :class="{ disabled: !hasSelection }">
              <el-tooltip :content="$t('components.mesh-editor.tooltip.mirror')" placement="left">
                <font-awesome-icon :icon="['fac', 'mirror']" />
              </el-tooltip>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item class="action" @click="mirrorSelected(AxisType.x)"> x </el-dropdown-item>
                <el-dropdown-item class="action" @click="mirrorSelected(AxisType.y)"> y </el-dropdown-item>
                <el-dropdown-item class="action" @click="mirrorSelected(AxisType.z)"> z </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
        <BoneTools
          ref="boneTools"
          v-model="modelValue"
          :trois-renderer="troisRenderer"
          :editor-mode="editorMode"
          :selection-list="selectionList"
          :view-ctrl="viewCtrl"
          :transform-tools="transformTools"
          :activate-bones="activateBones"
          :history-list="modelValue.historyList"
        />
        <TransformTools
          ref="transformTools"
          v-model="modelValue"
          :trois-renderer="troisRenderer"
          :editor-mode="editorMode"
          :selection-list="selectionList"
          :has-selection="hasSelection"
          :view-ctrl="viewCtrl"
          :can-transform="canTransform"
          :can-manual-transform="canManualTransform"
          :can-toggle-pivot-mode="canTogglePivotMode"
          :reset-on-pivot-changed="resetOnPivotChanged"
          :update-appearance="updateAppearance"
          :history-list="modelValue.historyList"
          :select-object="selectObject"
          :default-orientation-axis="defaultOrientationAxis"
          :active-modify-type="activeModifyType"
        />
      </div>
    </nav>

    <el-container class="webGlView" :class="{ fullscreen: fullscreen }">
      <div :class="{ 'without-footer': !isWorkflowFooter }" class="loading-text">
        <h6 v-if="loadingCircle" class="text-color text-size fw-semibold mb-2">
          {{ $t('views.configurator.modelLoading') }}
        </h6>
        <h6 v-else-if="isApplyingColors" class="text-color text-size fw-semibold mb-2">
          {{ $t('views.configurator.applyingColors') }}
        </h6>
      </div>
      <el-main id="main" v-loading="loadingCircle || isApplyingColors" element-loading-background="transparent" class="fillHorizontal">
        <OrientationGizmo v-show="showOrientationGizmo" ref="orientationGizmo"></OrientationGizmo>

        <div v-if="canPartSelect" class="hierarchy">
          <el-scrollbar>
            <el-space>
              <PreviewRendererImg
                :meshes="modelValue.treeData"
                img-size="5.8"
                :offset="1.5"
                :can-remove="true"
                :can-select="true"
                :is-selected="true"
                :can-take-snapshot="canTakeSnapshot"
                @select-item="selectPart"
                @take-snapshot="takeSnapshot"
              />
            </el-space>
          </el-scrollbar>
        </div>

        <div v-show="showSiderbarRightTools">
          <sidebar-right-tools
            :productTypeId="productTypeId" 
            :is-loading="loadingCircle"
            :is-loading-download-object="isLoadingDownloadObject"
            :trois-renderer="troisRenderer"
            :filename="modelValue?.treeData[0]?.name"
            :model-value="modelValue"
            :is-show-deformation-editor="isShowDeformationEditor"
            :is-deformation-active="isDeformationActive"
            :is-loading-deformation="isLoadingDeformation"
            :is-user-supplier="currentPage === 'customize-workflow' && isUserSupplier"
            @download3-d-file-to-obj="download3DFileToObj()"
            @show-product-parameter-modal-dialog="showProductParameterModalDialog()"
            @close-deformation-editor="closeDeformationEditor()"
            @showComponentsModalDialog="showComponentsModalDialog()"
          />
        </div>

        <Renderer
          v-if="isActive"
          ref="troisRenderer"
          antialias
          :orbit-ctrl="{
            enableDamping: false,
            dampingFactor: 0.05,
            enablePan: true,
          }"
          :resize="true"
          :alpha="true"
          :xr="true"
          :preserve-drawing-buffer="true"
        >
          <Camera :position="renderCameraPos" />
          <Scene ref="sceneRef">
          </Scene>
          <EffectComposer>
            <RenderPass />
          </EffectComposer>
        </Renderer>
      </el-main>
      <EmbossingSidebar
        v-if="canEmbossing && showSidebar"
        v-model="selectedEmbossing"
        :select-object="selectObject"
        :selection-list="selectionList"
        :history-list="modelValue.historyList"
      />
    </el-container>
    <div v-if="canPartSelect" class="hierarchy">
      <el-scrollbar>
        <el-space>
          <PreviewRendererImg
            :meshes="modelValue.treeData"
            img-size="5"
            :offset="1.5"
            :can-remove="true"
            :can-select="true"
            :is-selected="true"
            :can-take-snapshot="canTakeSnapshot"
            @select-item="selectPart"
            @take-snapshot="takeSnapshot"
          />
        </el-space>
      </el-scrollbar>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Ref, Prop, Watch } from 'vue-facing-decorator';
import { RendererPublicInterface } from 'troisjs';
import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import { ARButton } from 'three/examples/jsm/webxr/ARButton.js';
import { useRoute } from 'vue-router';
import { VertexNormalsHelper } from 'three/examples/jsm/helpers/VertexNormalsHelper.js';

import MeshTree from '@/components/three/scenes/MeshTree.vue';
import { MeshTreeData, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import * as THREEEnum from '@/types/enum/three';
import OrientationGizmo from '@/components/three/OrientationGizmo.vue';
import * as THREETransform from '@/utils/three/transform';
import * as THREEMaterial from '@/utils/three/material';
import * as THREEBoundingBox from '@/utils/three/boundingBox';
import * as THREEInit from '@/utils/three/init';
import * as THREEBone from '@/utils/three/bone';
import { EditorModeState, ProjectionCategory } from '@/types/enum/editor';
import { LayoutColor } from '@/types/enum/color';
import { ProductColors } from '@/services/api/productDbService';
import { HistoryOperationType } from '@/types/ui/HistoryList';
import { ViewCtrl } from '@/types/ui/ViewCtrl';
import { EditorMode } from '@/types/ui/EditorMode';
import { SelectionList } from '@/types/ui/SelectionList';
import TransformTools from '@/components/three/tools/TransformTools.vue';
import BoneTools from '@/components/three/tools/BoneTools.vue';
import * as LayoutUtility from '@/utils/layout';
import CurveTools from '@/components/three/tools/CurveTools.vue';
import DeformationTools from '@/components/three/tools/DeformationTools.vue';
import { addWebGlContext, deleteWebGlContext, waitFor } from '@/utils/three/webGlContext';
import ProjectionTools from '@/components/three/tools/ProjectionTools.vue';
import PreviewRendererImg from '@/components/features/previewRenderer/PreviewRendererImg.vue';
import ToggleSidebar from '@/components/ui-elements/ToggleSidebar.vue';
import EmbossingSidebar from '@/components/three/tools/EmbossingSidebar.vue';
import RenderTools from '@/components/three/tools/RenderTools.vue';
import { EmbossingExportData, FFDExportData } from '@/services/api/templateService';
import { CurveCategory } from '@/types/api/Model/Curve/CurveCategory';
import { CameraExportData, CurveExportData } from '@/services/api/modelService';
import ProductQuality from '@/components/three/ProductQuality.vue';
import { handleXRHitTest, resetHitTestSource } from '@/utils/three/hitTest';
import { createPlaneMarker } from '@/utils/three/planeMarker';
import * as componentService from '@/services/api/componentService';
import SidebarRightTools from '../tools/SidebarRightTools.vue';
import { registerShortcut, unregisterShortcut } from '@/utils/shortcuts';
import { EmbossingData } from '@/types/interfaces/projectionSidebarData';
import { markRaw } from 'vue';
import { MeshSizesType } from './CustomMeshLoader.vue';

@Component({
  components: {
    RenderTools,
    EmbossingSidebar,
    ToggleSidebar,
    ProjectionTools,
    DeformationTools,
    CurveTools,
    BoneTools,
    TransformTools,
    OrientationGizmo,
    MeshTree,
    PreviewRendererImg,
    ProductQuality,
    SidebarRightTools,
  },
  emits: [
    'takeSnapshot',
    'meshesLoaded',
    'selectionChanged',
    'showProductParameterModalDialog',
    'showComponentsModalDialog',
    'download3DFileToObj',
    'closeDeformationEditor',
    'getMeshSizes',
  ],
})
/* eslint-disable @typescript-eslint/no-explicit-any*/
export default class MeshEditor extends Vue {
  //#region properties
  @Prop() modelValue!: MeshTreeData;
  @Prop({ default: null }) productTypeId!: number | null;
  @Prop({ default: true }) canTransform!: boolean;
  @Prop({ default: true }) canManualTransform!: boolean;
  @Prop({ default: true }) canTogglePivotMode!: boolean;
  @Prop({ default: true }) canMirror!: boolean;
  @Prop({ default: true }) canCopy!: boolean;
  @Prop({ default: true }) canAline!: boolean;
  @Prop({ default: true }) canGroup!: boolean;
  @Prop({ default: true }) canSelect!: boolean;
  @Prop({ default: true }) canColorize!: boolean;
  @Prop({ default: true }) canToggleBones!: boolean;
  @Prop({ default: true }) canUndo!: boolean;
  @Prop({ default: true }) canDeform!: boolean;
  @Prop({ default: true }) canCurve!: boolean;
  @Prop({ default: false }) canPartSelect!: boolean;
  @Prop({ default: false }) canEmbossing!: boolean;
  @Prop({ default: false }) canRender!: boolean;
  @Prop({ default: false }) canToggleOpacity!: boolean;
  @Prop({ default: false }) activateBones!: boolean;
  @Prop({ default: false }) activateAlineToFloor!: boolean;
  @Prop({ default: false }) activateDisplayBoundingBox!: boolean;
  @Prop({ default: true }) activateDisplayGrid!: boolean;
  @Prop({ default: EditorModeState.default })
  activeEditorMode!: EditorModeState;
  @Prop({ default: THREEEnum.ModifyType.translate })
  activeModifyType!: THREEEnum.ModifyType;
  @Prop({ default: false }) fitCameraToScene!: boolean;
  @Prop({ default: false }) resetOnPivotChanged!: boolean;
  @Prop({ default: THREEEnum.OrientationAxis.none })
  defaultOrientationAxis!: THREEEnum.OrientationAxis;
  @Prop({ default: Object.values(CurveCategory) })
  allowedCurveCategories!: CurveCategory[];
  @Prop({ default: false }) canTakeSnapshot!: boolean;
  @Prop({ default: true }) hasNavigationBar!: boolean;
  @Prop({ default: false }) canXr!: boolean;
  @Prop({ default: false }) hasToolbar!: boolean;
  @Prop({ default: false }) isUserSupplier!: boolean;
  @Prop({ default: true }) fullscreen!: boolean;
  @Prop({ default: false }) showOrientationGizmo!: boolean;
  @Prop({ default: false }) isLoadingDownloadObject!: boolean;
  @Prop({ default: false }) showSiderbarRightTools!: boolean;
  @Prop({ default: false }) isShowDeformationEditor!: boolean;
  @Prop({ default: false }) isDeformationActive!: boolean;
  @Prop({ default: false }) isLoadingDeformation!: boolean;
  @Prop({ default: false }) canCalculateResult!: boolean;
  @Prop({ default: false }) isApplyingColors!: boolean;
  @Prop({ default: true }) isWorkflowFooter?: boolean;

  @Ref('sceneRef') readonly sceneRef!: THREE.Scene;

  route = useRoute();
  //loading
  isMounted = false;
  colors: string[] = ProductColors.default;
  uuid = uuidv4();
  isActive = false;
  isXrActive = false;

  //renderer
  troisRenderer: RendererPublicInterface | null = null;
  troisRendererDeformation: RendererPublicInterface | null = null;

  // render camera
  view: string = THREEEnum.Views.custom;
  renderCameraPos: THREE.Vector3 = new THREE.Vector3(4, 4, 4);
  pointLightPos: THREE.Vector3 = new THREE.Vector3(4, 4, 4);
  pointLight!: THREE.DirectionalLight; // add this
  renderCameraPosDeformation: THREE.Vector3 = new THREE.Vector3(4, 4, 4);
  pointLightPosDeformation: THREE.Vector3 = new THREE.Vector3(4, 4, 4);
  lightDelta = new THREE.Vector3(1, 1, 1);
  viewCtrl!: ViewCtrl;
  viewCtrlDeformation!: ViewCtrl;

  // grid
  grid!: THREE.GridHelper;
  displayGrid = false;
  displayVertexNormals = false;

  // editor mode
  editorMode: EditorMode = new EditorMode();

  // transform
  transformTools: InstanceType<typeof TransformTools> | null = null;

  //boundingBox
  displayBoundingBox = false;

  //bones
  boneTools: InstanceType<typeof BoneTools> | null = null;

  //deformation
  deformationTools: InstanceType<typeof DeformationTools> | null = null;

  //curve
  curveTools: InstanceType<typeof CurveTools> | null = null;

  //embossing
  projectionTools: InstanceType<typeof ProjectionTools> | null = null;
  showSidebar = true;
  selectedEmbossing: EmbossingData = {
    id: null,
    activeCategory: ProjectionCategory.custom,
    projection: [
      {
        projectionCategory: ProjectionCategory.custom,
        aspectRatio: 1,
        scaleFactor: 1,
        offset: { x: 0, y: 0 },
        embossingFile: null,
        color: '#ffffff',
        embossingDefinition: [],
        colorDefinition: {},
      },
    ],
  };

  //render
  renderTools: InstanceType<typeof RenderTools> | null = null;

  //selection
  hasSelection = false;
  selectionList!: SelectionList;
  selectedName = '';

  // enums for use in template section
  Views = THREEEnum.Views;
  AxisType = THREEEnum.AxisType;

  // loading circle
  loadingCircle = false;

  controller = new AbortController();
  abortSignal: AbortSignal | null = null;

  arMeshList: THREE.Object3D[] = [];

  isXrInitialized = false;
  //#endregion properties

  //#region scene design
  mounted(): void {
    waitFor(() => addWebGlContext(this.uuid)).then(() => {
      this.isActive = true;
      setTimeout(() => {
        this.initEditor();
        //this.onModelValueStructureChanged();
      }, 100);
    });
    if (navigator.xr) {
      navigator.xr.isSessionSupported('immersive-ar').then((supported) => {
        this.isXrActive = supported;
      });
    }
    registerShortcut('+', this, () => this.zoom(0.1));
    registerShortcut('-', this, () => this.zoom(-0.1));
    registerShortcut('f', this, () => this.fitCameraToSelection());
  }

  unmounted(): void {
    if (this.troisRenderer) {
      this.troisRenderer.renderer.dispose();
      setTimeout(() => {
        this.troisRenderer?.renderer.forceContextLoss();
      }, 100);
    }
    deleteWebGlContext(this.uuid);
    this.removeXrApp();
    const orbitCtrl = this.troisRenderer?.three.cameraCtrl;
    if (orbitCtrl) {
      // setup camera control
      orbitCtrl.removeEventListener('change', this.changeOrbitCtrl);
      orbitCtrl.removeEventListener('start', this.startOrbitCtrl);
    }
    this.troisRenderer?.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.troisRenderer?.renderer.domElement.removeEventListener('mousemove', this.onPointerMove);
    this.troisRenderer?.renderer.domElement.removeEventListener('pointerup', this.onPointerUp);
    unregisterShortcut('+', this);
    unregisterShortcut('-', this);
    unregisterShortcut('f', this);
  }

  setTroisRenderer(): void {  
    if (this.$refs.troisRenderer) {
      this.troisRenderer = markRaw(this.$refs.troisRenderer as RendererPublicInterface); 
      this.troisRenderer.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;    
      THREE.ColorManagement.enabled = false;  
    }  
    
    if (this.$refs.troisRendererDeformation) {    
      this.troisRendererDeformation = markRaw(this.$refs.troisRendererDeformation as RendererPublicInterface);
    }  
      
    if (this.isXrActive && this.canXr) {    
      this.initializeXrApp();  
    }
  }

  removeXrApp() {
    this.isXrInitialized = false;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.xr.enabled = false;
    const arButton = document.getElementById('xr-button');
    if (arButton) document.body.removeChild(arButton);
  }

  initializeXrApp(): void {
    this.isXrInitialized = true;
    const { devicePixelRatio, innerHeight, innerWidth } = window;

    // Create a new WebGL renderer and set the size + pixel ratio.
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(innerWidth, innerHeight);
    renderer.setPixelRatio(devicePixelRatio);

    // Enable XR functionality on the renderer.
    renderer.xr.enabled = true;

    // Add it to the DOM.
    // document.body.appendChild( renderer.domElement );

    // Create the AR button element, configure our XR session, and append it to the DOM.
    const arButton = ARButton.createButton(renderer, {
      optionalFeatures: ['dom-overlay'],
      requiredFeatures: ['local', 'hit-test'],
    });
    arButton.removeAttribute('style');
    arButton.classList.add(...['btn', 'md-2']);
    arButton.id = 'xr-button';
    arButton.hidden = true;

    document.body.appendChild(arButton);

    // Pass the renderer to the createScene-funtion.
    this.createXrScene(renderer);
  }

  createXrScene(renderer: THREE.WebGLRenderer): void {
    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.02, 20);

    /**
     * Add some simple ambient lights to illuminate the model.
     */
    const ambientLight = new THREE.AmbientLight(0x808080, 1.0);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0xffffff, 0.55);
    camera.add(pointLight);
    scene.add(camera);

    /**
     * Create the plane marker to show on tracked surfaces.
     */
    const planeMarker = createPlaneMarker();
    scene.add(planeMarker);

    /**
     * Setup the controller to get input from the XR space.
     */
    const controller = renderer.xr.getController(0);
    scene.add(controller);

    const clearXrScene = () => {
      for (let i = scene.children.length - 1; i >= 0; i--) {
        const child = scene.children[i];
        if (child instanceof THREE.Group) {
          scene.remove(child);
        }
      }
    };

    const updateXrMeshList = () => {
      this.arMeshList = [];
      for (const item of this.modelValue.treeData) {
        this.arMeshList.push(item.mesh);
      }
    };

    /**
     * The onSelect function is called whenever we tap the screen
     * in XR mode.
     */
    const onSelect = () => {
      if (planeMarker.visible) {
        clearXrScene();

        const group = new THREE.Group();
        for (const item of this.arMeshList.filter((m) => m !== undefined)) {
          const model = item.clone(true);
          model.visible = true;
          group.add(model);
        }

        // Place the model on the spot where the marker is showing.
        group.position.setFromMatrixPosition(planeMarker.matrix);
        group.scale.set(0.001, 0.001, 0.001);

        const bbox = new THREE.Box3().setFromObject(group);
        group.position.x += group.position.x - bbox.min.x - (bbox.max.x - bbox.min.x) / 2;
        group.position.y += group.position.y - bbox.min.y;
        group.position.z += group.position.z - bbox.min.z;
        group.visible = true;

        scene.add(group);
      }
    };

    controller.addEventListener('select', onSelect);

    /**
     * Called whenever a new hit test result is ready.
     */
    const onHitTestResultReady = (hitPoseTransformed: Float32Array) => {
      if (hitPoseTransformed) {
        planeMarker.visible = true;
        planeMarker.matrix.fromArray(hitPoseTransformed);
      }
    };

    /**
     * Called whenever the hit test is empty/unsuccesful.
     */
    const onHitTestResultEmpty = () => {
      planeMarker.visible = false;
    };

    const renderLoop = (timestamp: number, frame?: any) => {
      if (renderer.xr.isPresenting) {
        if (frame) {
          handleXRHitTest(renderer, frame, onHitTestResultReady, onHitTestResultEmpty);
        }
        renderer.render(scene, camera);
      }
    };

    renderer.xr.addEventListener('sessionend', () => {
      renderer.setAnimationLoop(null);
      resetHitTestSource();
      clearXrScene();
    });
    renderer.xr.addEventListener('sessionstart', () => {
      renderer.setAnimationLoop(renderLoop);
      updateXrMeshList();
    });
  }

  get orientationGizmo(): OrientationGizmo | null {
    return this.$refs.orientationGizmo as OrientationGizmo;
  }

  get orientationGizmoDeformation(): OrientationGizmo | null {
    return this.$refs.orientationGizmoDeformation as OrientationGizmo;
  }

initEditor(): void {
  LayoutUtility.refresh();
  this.setTroisRenderer();

  this.viewCtrl = markRaw(new ViewCtrl(this.troisRenderer!, this.orientationGizmo, this.fitCameraToScene));

  if (!this.troisRenderer) return;

  this.selectionList = new SelectionList(this.modelValue, this.troisRenderer);

  if (this.troisRenderer.scene && this.troisRenderer.camera) {
    this.setupMainScene(this.troisRenderer, this.orientationGizmo);
  }

  if (this.troisRendererDeformation?.scene && this.troisRendererDeformation?.camera) {
    this.setupMainScene(this.troisRendererDeformation, this.orientationGizmoDeformation);
  }

  this.isMounted = true;
  this.initToolRefs();
}

private setupMainScene(
  renderer: RendererPublicInterface | null,
  gizmo: OrientationGizmo | null
): void {
  gizmo?.init(this.troisRenderer!);

  const orbitCtrl = renderer?.three.cameraCtrl;
  orbitCtrl?.addEventListener('change', this.changeOrbitCtrl);
  orbitCtrl?.addEventListener('start', this.startOrbitCtrl);

  this.grid = markRaw(new THREE.GridHelper(10, 50, LayoutColor.primary, 0x444444));
  this.grid.visible = this.displayGrid;
  renderer?.scene?.add(this.grid);

  const ambientLight = new THREE.AmbientLight(0xffffff, 2);
  renderer?.scene?.add(ambientLight);

  this.pointLight = new THREE.DirectionalLight(0xffffff, 1.2);
  this.pointLight.position.copy(this.pointLightPos);
  renderer?.scene?.add(this.pointLight);

  if (renderer === this.troisRenderer) {
    renderer?.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
    renderer?.renderer.domElement.addEventListener('mousemove', this.onPointerMove);
    renderer?.renderer.domElement.addEventListener('pointerup', this.onPointerUp);
  }
}

    private async initToolRefs(): Promise<void> {
      setTimeout(() => {
        this.transformTools = this.$refs.transformTools as InstanceType<typeof TransformTools>
        this.boneTools = this.$refs.boneTools as InstanceType<typeof BoneTools>
        this.deformationTools = this.$refs.deformationTools as InstanceType<typeof DeformationTools>
        this.projectionTools = this.$refs.projectionTools as InstanceType<typeof ProjectionTools>
        this.curveTools = this.$refs.curveTools as InstanceType<typeof CurveTools>
        this.renderTools = this.$refs.renderTools as InstanceType<typeof RenderTools>
      }, 100);
    }
  startOrbitCtrl(): void {
    this.view = THREEEnum.Views.custom;
  }

  changeOrbitCtrl(): void {
    this.viewCtrl.updateOrientationGizmo();
    if (this.boneTools) this.boneTools.updateBones();
    const camera = this.troisRenderer?.camera;
    if (camera) {
      const cameraDistance = this.viewCtrl.getCameraDistance();
      if (cameraDistance) {
        const lightPos = camera.localToWorld(this.lightDelta.clone().multiplyScalar(cameraDistance / 2));
        this.pointLightPos.copy(lightPos);

        // Update actual pointLight position in scene
        this.pointLight.position.copy(this.pointLightPos);

        // Update grid scale etc.
        const gridScaleFactor = cameraDistance / 20;
        this.grid.scale.set(gridScaleFactor, gridScaleFactor, gridScaleFactor);
      }
    }
  }

  getObjectById(id: string, searchList: THREE.Object3D[] | null = null): THREE.Object3D | null {
    if (searchList === null) {
      const scene = this.troisRenderer?.scene;
      if (scene) searchList = scene?.children;
    }
    if (searchList) {
      for (const item of searchList) {
        if (item.uuid === id) return item;
        const result = this.getObjectById(id, item.children);
        if (result) return result;
      }
    }
    return null;
  }

  getObjectByApiId(id: string, searchList: THREE.Object3D[] | null = null): THREE.Object3D | null {
    if (searchList === null) {
      const scene = this.troisRenderer?.scene;
      if (scene) searchList = scene?.children;
    }
    if (searchList) {
      for (const item of searchList) {
        const apiData = (item as any).apiData as MeshTreeDataItem;
        if (apiData?.uuid === id) return item;
        const result = this.getObjectByApiId(id, item.children);
        if (result) return result;
      }
    }
    return null;
  }

  getThreeMeshVectorSize(item: MeshTreeDataItem, object: any): MeshSizesType {
    // Get bounding box size (X: width, Y: height, Z: depth)
    const box = new THREE.Box3().setFromObject(object);
    const size = new THREE.Vector3();
    box.getSize(size);
    const meshSizes: MeshSizesType = {
      uuid: item.uuid,
      vector: {
        width: size.x,
        height: size.y,
        depth: size.z,
      }
    }
    return meshSizes
  }

  @Watch('modelValue.lastUpdateStructure', { immediate: false, deep: false })
  async onModelValueStructureChanged(): Promise<void> {
    if (this.abortSignal) {
      this.controller.abort();
      this.controller = new AbortController();
    }
    const getAllSceneObjects = (
      parent: THREE.Object3D | THREE.Scene
    ): {
      item: THREE.Object3D;
      parent: THREE.Object3D | THREE.Scene;
    }[] => {
      const customMeshList = parent.children
        .filter((object) => (object as any).apiData)
        .map((item) => {
          return {
            item: item,
            parent: parent,
          };
        });
      for (const item of parent.children) {
        customMeshList.push(...getAllSceneObjects(item));
      }
      return customMeshList;
    };

    const loadTreeData = async (items: MeshTreeDataItem[], signal: AbortSignal, parent: MeshTreeDataItem | null = null): Promise<void> => {
      try {
        const scene = this.troisRenderer?.scene;
        if (scene) {
          for (const item of items) {
            // !item.scene => prevent double loading during update
            if (!item.isNone && !item.scene) {
              item.scene = scene;
              await THREEInit.initObject3D(item, parent, false).then((object) => {
                if (signal.aborted) {
                  throw Error('AbortError');
                }
                if (object && !parent?.isGroup) {
                  item.scene.add(object);
                  // Get bounding box size (X: width, Y: height, Z: depth)
                  const meshSizes: MeshSizesType = this.getThreeMeshVectorSize(item, object)
                  this.$emit('getMeshSizes', meshSizes)
                  if (!this.canSelect && items[0].uuid === object.uuid) this.selectObject(object, true, true, true);
                }
              });
            } else if (item.isCamera) {
              (item.camera as any).apiData = item;
            }
            if (item.children) await loadTreeData(item.children, signal, item);
            for (const child of item.children) {
              const newParent = item.isNone ? scene : item.mesh;
              if (child.mesh) {
                const childMesh = this.getObjectById(child.mesh.uuid);
                if (childMesh && childMesh.parent && childMesh.parent.uuid !== newParent.uuid) {
                  const oldParent = childMesh.parent;
                  let childWorldPosition = childMesh.position;
                  if (oldParent && !(oldParent as any).isScene) {
                    childWorldPosition = oldParent.localToWorld(childMesh.position);
                  }
                  newParent.add(childMesh);
                  childMesh.position.copy(newParent.worldToLocal(childWorldPosition));
                }
              }
            }
            if (item.mesh && item.isSelected && !this.selectionList.isSelected(item)) await this.selectTreeItem(item);
          }
        }
      } catch (e: any) {
        if (e.message === 'AbortError') {
          cleanupTreeData();
          this.loadingCircle = false;
        }
      }
    };
    const cleanupTreeData = (): void => {
      if (this.troisRenderer?.scene) {
        const scene = this.troisRenderer.scene;
        const customMeshList = getAllSceneObjects(scene);
        customMeshList.forEach((object) => {
          if ((object.item as any).apiData && !this.modelValue.exists((object.item as any).apiData.uuid)) {
            if (this.transformTools && this.selectionList.getSelectedObjects().find((child) => child.uuid === object.item.uuid)) {
              this.selectionList.deselectObject(object.item);
              scene.remove(this.transformTools?.transformControl?.object);
            }
            object.parent.remove(object.item);
            this.modelValue.historyList.remove((object.item as any).apiData.uuid, null);
          }
        });
      }
    };

    await waitFor(() => this.isMounted);
    if (this.troisRenderer) {
      cleanupTreeData();
      this.loadingCircle = true;
      if (this.modelValue.treeData.length > 0) {
        this.abortSignal = this.controller.signal;
        await loadTreeData(this.modelValue.treeData, this.abortSignal).finally(() => {
          this.abortSignal = null;
          this.loadingCircle = false;
          this.$emit('meshesLoaded');
        });
      } else {
        this.loadingCircle = true;
      }

      if (this.modelValue.treeData.length > 0) {
        this.viewCtrl.fitControlCameraToScene();
      }
    }
  }

  @Watch('activateDisplayBoundingBox', { immediate: true })
  onActivateDisplayBoundingBoxChanged(): void {
    this.displayBoundingBox = this.activateDisplayBoundingBox;
  }

  @Watch('fitCameraToScene', { immediate: true })
  fitCameraToSceneChanged(): void {
    if (this.viewCtrl) this.viewCtrl.fitCameraToScene = this.fitCameraToScene;
  }

  fitCameraToSelection(): void {
    this.viewCtrl.fitControlCameraToSelection(this.selectionList.getSelectedObjects());
  }

  @Watch('modelValue.lastUpdateVisibility', { immediate: false, deep: false })
  async onModelValueVisibilityChanged(): Promise<void> {
    const syncData = async (items: MeshTreeDataItem[]): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const item of items) {
        if (item.children && item.children.length > 0) await syncData(item.children);
        if (item.syncData) {
          item.syncData = false;
          if (item.mesh && item.opacity) await THREEMaterial.setOpacity(item.mesh, item.opacity);
          await THREEMaterial.setWireframe(item.mesh, item.showWireframe);
          if ((item.mesh || item.camera) && item.isSelected && !this.selectionList.isSelected(item)) await this.selectTreeItem(item);
          else if (item.isSelected && !this.selectionList.isSelected(item)) this.loadSelectionName(item);
        }
      }
    };
    if (this.boneTools) this.boneTools.updateBones();

    if (this.troisRenderer) {
      await syncData(this.modelValue.treeData);
    }
    this.hasSelection = this.selectionList && this.selectionList.count > 0;
    this.hasSelection = this.selectionList && this.selectionList.count > 0;
  }

  async syncOpacity(): Promise<void> {
    const syncData = async (items: MeshTreeDataItem[]): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const item of items) {
        if (item.children && item.children.length > 0) await syncData(item.children);
        if (item.mesh && item.opacity) {
          await THREEMaterial.setOpacity(item.mesh, item.opacity);
        }
      }
    };

    if (this.troisRenderer) {
      await syncData(this.modelValue.treeData);
    }
  }
  //#endregion scene design

  //#region calculate size
  getSelectionSize(): THREE.Vector3 {
    if (this.selectionList) {
      return THREEBoundingBox.getSelectionSize(this.selectionList.getSelectedObject(), this.modelValue.getMeshList());
    }
    return THREEBoundingBox.getSceneSize(this.modelValue.getMeshList());
  }

  getSelectionFloorCenter(): THREE.Vector3 {
    if (this.selectionList) {
      const selection = this.selectionList.getSelectedObject();
      if (selection) {
        const box = THREEBoundingBox.getBoundingBox([selection]);
        const center = box.getCenter(new THREE.Vector3());
        return new THREE.Vector3(center.x, box.min.y, center.z);
      }
    }
    return new THREE.Vector3(0, 0, 0);
  }
  //#endregion calculate size

  //#region selection
  loadSelectionName(item: MeshTreeDataItem | null = null): void {
    if (item) {
      this.selectedName = item.name;
    } else {
      const selection = this.modelValue.getSelection();
      if (selection.length > 0) {
        this.selectedName = selection[0].name;
      } else this.selectedName = '';
    }
  }
  //@Watch('selectedName', { immediate: true })
  saveName() {
    const selection = this.modelValue.getSelection();
    if (selection.length > 0 && selection[0].name !== this.selectedName) {
      selection[0].name = this.selectedName;
      if (this.selectedName && this.modelValue.modelId && selection[0].componentId) {
        componentService.changeName(this.modelValue.modelId, selection[0].componentId, selection[0].uuid, this.selectedName);
      }
    }
  }

  mouseDownPosition!: THREE.Vector2;
  onPointerDown(event: MouseEvent): void {
    if (this.transformTools) this.transformTools?.transformStart();
    this.mouseDownPosition = new THREE.Vector2(event.offsetX, event.offsetY);
    if (this.transformTools && this.editorMode.isOrientationMode) this.transformTools?.updateStartPoint(event);
  }

  onPointerMove(event: MouseEvent): void {
    if (this.transformTools && this.editorMode.isOrientationMode) {
      this.transformTools?.updateEndPoint(event);
    }
  }

  private handleShortClick(event: MouseEvent): void {
    if (this.editorMode.isBoneMode && this.boneTools && this.boneTools.intersectActive) {
      this.handleBoneModeClick(event);
    } else if (this.canSelect) {
      this.handleSelectionModeClick(event);
    }
  }

  private handleBoneModeClick(event: MouseEvent): void {
    this.boneTools!.updateBonePosition(event);
    const selection = this.modelValue.getSelection();
    if (selection.length > 0) {
      const targetBone = this.selectionList.getClickedObject(event, false, 'Bone');
      if (targetBone) {
        selection[0].activeBoneIndex = this.boneTools!.boneContainer.children.indexOf(targetBone);
        this.modelValue.updateVisibility();
      }
    }
  }

  private handleSelectionModeClick(event: MouseEvent): void {
    const specialTarget = this.selectionList.getClickedObject(event, true, null, true);
    const target = this.selectionList.getClickedObject(event, true, null, false);

    if (target && (target as any).apiData && (target as any).apiData.name === 'Reißverschluss') {
      return;
    }

    if (!specialTarget || target) {
      this.selectObject(target);
    }
  }

  onPointerUp(event: MouseEvent): void {
    if (!this.mouseDownPosition) {
      this.onPointerMove(event);
      if (this.transformTools && this.editorMode.isOrientationMode) {
        this.transformTools?.finishIntersection();
      }
      return;
    }
    
    const movingDistance = this.mouseDownPosition.distanceTo(new THREE.Vector2(event.offsetX, event.offsetY));
    if (movingDistance < 20) {
      this.handleShortClick(event);
    }
  }

  async selectMesh(meshId: string): Promise<void> {
    const mesh = this.getObjectByApiId(meshId);
    await this.selectObject(mesh);
  }

  private editorSelectionMode: EditorModeState = EditorModeState.default;
  async selectObject(target: THREE.Object3D | null, setSelectionFlag = true, deselectPrevious = true, triggerUpdate = false): Promise<void> {
    const objectUUID = this.selectionList.getSelectedObject()?.uuid
    if (objectUUID === target?.uuid && this.editorMode.editorMode === this.editorSelectionMode) {
      return;
    }
    if (target && (target as any).apiData) {
      const sceneTarget = this.getObjectByApiId((target as any).apiData.uuid);
      if (sceneTarget) target = sceneTarget;
    }
    this.editorSelectionMode = this.editorMode.editorMode;
    if (this.transformTools) await this.transformTools?.setTransformControlsForTarget(target);
    await this.selectionList.selectObject(target, setSelectionFlag, deselectPrevious, triggerUpdate);
    this.loadSelectionName();

    await this.renderTarget(target)
    this.renderDeformationTools()
    this.renderProjectionObject()
    this.renderCurveObject()
    this.renderToolsObject()
    

    this.$emit('selectionChanged', target);
    this.hasSelection = this.selectionList.count > 0;

    this.calculateNormalsForSelectedObject();
  }

  async renderTarget(target:THREE.Object3D<THREE.Object3DEventMap> | null){
    if (target) {
      this.updateModeOpacity(target);
      this.modelValue.updateVisibility();
      if (this.displayBoundingBox) this.updateBoundingBox([target]);
      else if (this.transformTools) {
        target.add(this.transformTools?.centerPoint);
      }
      if (this.boneTools) this.boneTools.updateBones();
    } else {
      if (this.editorMode.isPivotMode || this.editorMode.isOrientationMode || this.editorMode.isBoneMode) {
        await this.syncOpacity();
      }
    }
  }

  renderDeformationTools(){
    if (this.deformationTools) {
      this.deformationTools.setToolForSelection();
    }
  }

  renderProjectionObject(){
    if (this.projectionTools) {
      this.projectionTools.createProjectionReferenceForSelectedObject();
    }
  }

  renderCurveObject(){
    if (this.curveTools) {
      this.curveTools.selectedObjectChanged();
    }
  }

  renderToolsObject(){
    if (this.renderTools) {
      this.renderTools.selectedObjectChanged();
    }
  }

  calculateNormalsForSelectedObject(): void {
    const target = this.selectionList.getSelectedObject();
    if (this.troisRenderer?.scene && target && (target as any).isMesh && this.displayVertexNormals) {
      const selectionSize = this.getSelectionSize();
      const vertexNormalsHelper = new VertexNormalsHelper(target as any, selectionSize.x / 20, 0x00ff00);
      const previousVertex = this.troisRenderer.scene.children.filter((item) => item.type === 'VertexNormalsHelper');
      if (previousVertex.length > 0) {
        this.troisRenderer.scene.remove(...previousVertex);
      }
      this.troisRenderer.scene.add(vertexNormalsHelper);
    } else if (this.troisRenderer?.scene) {
      const vertexHelper = this.troisRenderer.scene.children.filter((item) => item.type === 'VertexNormalsHelper');
      if (vertexHelper.length > 0) {
        this.troisRenderer.scene.remove(...vertexHelper);
      }
    }
  }

  private updateBoundingBox(selection: THREE.Object3D[]): void {
    let center: THREE.Vector3 | null = null;
    if (this.transformTools && selection.length > 0) {
      const selectionObject = selection[0];
      selectionObject.add(this.transformTools?.centerPoint);
      if (this.displayBoundingBox) {
        center = THREEBoundingBox.addBoundingBox(selection);
      } else {
        const boundingBox = THREEBoundingBox.getBoundingBox(selection);
        center = boundingBox.getCenter(new THREE.Vector3());
      }
      if (center) this.transformTools?.centerPoint.position.copy(selectionObject.worldToLocal(center));
      this.transformTools?.centerPoint.rotation.copy(this.transformTools?.pivot.rotation);
      this.viewCtrl.adaptPointerSize(this.transformTools?.centerPoint);
    }
  }

  async selectTreeItem(target: MeshTreeDataItem | null): Promise<void> {
    if (target) {
      if (!this.selectionList.isSelected(target)) {
        const list = this.selectionList.getSceneObjects(null, false);
        const sceneTarget = list.find((item) => item.uuid === target.uuid);
        if (sceneTarget) await this.selectObject(sceneTarget, false);
        else if (target.mesh) await this.selectObject(target.mesh, false);
        else if (target.camera) await this.selectObject(target.camera, false);
      }
    } else {
      await this.selectObject(null, false);
    }
  }
  //#endregion selection

  //#region editor mode
  @Watch('activeEditorMode', { immediate: true })
  async onActiveEditorModeChanged(): Promise<void> {
    this.editorMode.state = this.activeEditorMode;
    if (this.selectionList) {
      const selection = this.selectionList.getSelectedObject();
      this.updateModeOpacity(selection);
    }
    if (this.transformTools) {
      this.transformTools?.modifyPivot();
      this.transformTools?.setOrientationDirection(this.defaultOrientationAxis, false);
    }
  }

  updateModeOpacity(target: THREE.Object3D | null): void {
    const opacity = this.editorMode.getModeOpacity();
    if (opacity < 100 && this.canTransform) {
      if (target) {
        THREEMaterial.setOpacity(target, opacity);
      }
    } else {
      this.syncOpacity();
    }

    if (this.transformTools && this.boneTools) {
      switch (this.editorMode.state) {
        case EditorModeState.pivot:
          THREEMaterial.setOpacity(this.transformTools?.pivot, 100);
          break;
        case EditorModeState.orientation:
          THREEMaterial.setOpacity(this.transformTools?.pivot, 100);
          THREEMaterial.setOpacity(this.transformTools?.arrowHelper, 100);
          break;
        case EditorModeState.bone:
          THREEMaterial.setOpacity(this.boneTools.boneContainer, 100);
          break;
      }
    }
  }

  get usePivotMode(): boolean {
    if (this.transformTools) {
      return this.editorMode.usePivotMode(this.transformTools?.orientationDirection);
    }
    return false;
  }
  //#endregion editor mode

  //#region tools
  @Watch('canEmbossing', { immediate: true })
  @Watch('showSidebar', { immediate: true })
  onShowSidebarChanged(): void {
    LayoutUtility.refresh();
  }

  colorizeSelected(color: string): void {
    const selection = this.selectionList.getSelectedObject();
    if (selection) {
      const oldColor = THREEMaterial.getColor(selection);
      this.colorize(selection, color);
      this.modelValue.historyList.add(
        (selection as any).apiData.uuid,
        HistoryOperationType.color,
        async () => this.colorize(selection, oldColor),
        async () => this.colorize(selection, color)
      );
    }
  }

  colorize(mesh: THREE.Object3D, color: string): void {
    THREEMaterial.setColor(mesh, color);
    this.modelValue.updateDB((mesh as any).apiData.uuid);
  }

  mirrorSelected(axis: THREEEnum.AxisType): void {
    const meshId = this.selectionList.getSelectedObjectUuid();
    if (meshId) {
      this.mirror(this.selectionList.getSelectedObjects(), axis);
      this.modelValue.historyList.add(
        meshId,
        HistoryOperationType.mirror,
        async () => this.mirror(this.selectionList.getSelectedObjects(), axis),
        async () => this.mirror(this.selectionList.getSelectedObjects(), axis)
      );
    }
  }

  mirror(meshes: THREE.Object3D[], axis: THREEEnum.AxisType): void {
    THREETransform.mirror(meshes, axis);
    for (const selection of meshes) {
      this.modelValue.updateDB((selection as any).apiData.uuid);
    }
    this.updateBoundingBox(this.selectionList.getSelectedObjects());
    this.calculateNormalsForSelectedObject();
  }

  copySelected(axis: THREEEnum.AxisType): void {
    const selection = this.selectionList.getSelectedObjects();
    selection.forEach((object) => {
      const bounds = THREEBoundingBox.getBoundingBox([object], false);
      const item = (object as any).apiData as MeshTreeDataItem;
      if (item) {
        const copy = this.modelValue.copy(item);
        if (copy) {
          THREETransform.shiftObject(copy, axis, bounds);
        }
      } else {
        const copy = THREETransform.copyObject(object);
        if (copy) {
          THREETransform.shiftObject(copy, axis, bounds);
          if (this.troisRenderer?.scene) this.troisRenderer.scene.add(copy);
          this.selectObject(copy);
        }
      }
    });
  }

  toggleGrid(): void {
    this.displayGrid = !this.displayGrid;
    if (this.grid) this.grid.visible = this.displayGrid;
  }

  toggleVertexNormals(): void {
    this.displayVertexNormals = !this.displayVertexNormals;
    this.calculateNormalsForSelectedObject();
  }

  toggleBoundingBox(): void {
    this.displayBoundingBox = !this.displayBoundingBox;
    if (this.displayBoundingBox) THREEBoundingBox.addBoundingBox(this.selectionList.getSelectedObjects());
    else THREEBoundingBox.removeBoundingBox(this.selectionList.getSelectedObjects());
  }

  toggleBones(): void {
    if (this.boneTools) this.boneTools.toggleBones();
  }

  alineSelectedToFloor(): void {
    const meshId = this.selectionList.getSelectedObjectUuid();
    if (meshId) {
      const oldPositions = this.selectionList.getSelectedObjects().map((item) => {
        return {
          mesh: item,
          position: item.position.clone(),
        };
      });
      this.alineToFloor(this.selectionList.getSelectedObjects());
      this.modelValue.historyList.add(
        meshId,
        HistoryOperationType.aline,
        async () => {
          for (const item of oldPositions) {
            item.mesh.position.copy(item.position.clone());
          }
        },
        async () => this.alineToFloor(this.selectionList.getSelectedObjects())
      );
    }
  }

  alineToFloor(meshes: THREE.Object3D[]): void {
    THREETransform.alineToFloor(meshes);
    const treeData = this.modelValue.getSelection();
    for (const selection of meshes) {
      this.modelValue.updateDB((selection as any).apiData.uuid);
      if (this.modelValue.modelId && treeData.length > 0 && treeData[0].componentId) {
        componentService.transformComponent(this.modelValue.modelId, treeData[0].componentId, treeData[0].uuid, selection.position);
      }
    }
  }

  groupObject(): void {
    const selection = this.modelValue.getSelection();
    if (selection.length > 0) {
      const selectionItem = selection[0];
      const parentItem = this.modelValue.getParent(selectionItem);
      if (parentItem) {
        const index = parentItem.children.indexOf(selectionItem);
        if (index > -1) {
          parentItem.children.splice(index, 1);
        }
      }
      const group = this.modelValue.addGroup('Group', [], new THREE.Vector3(0, 0, 0), new THREE.Euler(0, 0, 0), new THREE.Vector3(1, 1, 1), parentItem);
      group.children.push(selectionItem);
      group.updateActiveChildren();
      this.modelValue.updateDB(selectionItem.uuid);
      this.modelValue.updateStructure();
      this.modelValue.selectItem(group, true, false);
    }
  }
  //#endregion tools

  //#region history
  undo(): void {
    this.modelValue.historyList.goBack();
    this.updateHelper();
  }

  redo(): void {
    this.modelValue.historyList.goForward();
    this.updateHelper();
  }

  updateHelper(): void {
    this.viewCtrl.fitControlCameraToScene();
    if (this.transformTools) this.viewCtrl.adaptPointerSize(this.transformTools?.pivot);
    if (this.boneTools) this.boneTools.updateBones();
  }
  //#endregion history

  //#region pivot
  updateAppearance(): void {
    const pivotBoneActive = THREEBone.pivotBoneActive(this.modelValue);
    if (this.usePivotMode || this.editorMode.isOrientationMode || (this.editorMode.isBoneMode && pivotBoneActive)) {
      this.updateBoundingBox(this.selectionList.getSelectedObjects());
    }
  }
  //#endregion pivot

  //#region camera
  zoom(factor: number): void {
    this.viewCtrl.zoom(factor);
  }

  changeView(value: any): void {
    this.view = value;
    this.viewCtrl.changeView(value);
  }
  //#endregion camera

  //#region customize
  selectPart(data: MeshTreeDataItem): void {
    this.modelValue.selectItem(data, true, true);
  }

  public calculateResult(data: MeshTreeDataItem): void {
    if (this.curveTools) {
      this.curveTools.calculateResult(data);
    }
  }
  //#endregion customize

  //#region import / export
  exportFfdForMeshId(meshId: string): FFDExportData | null {
    if (this.deformationTools) return this.deformationTools.exportFfdForMeshId(meshId);
    return null;
  }

  importFfdForMeshId(meshId: string, spanCount: number[], gridPositionList: THREE.Vector3[]): void {
    if (this.deformationTools) this.deformationTools.importFfdForMeshId(meshId, spanCount, gridPositionList);
  }

  exportEmbossingForMeshId(meshId: string): EmbossingExportData[] {
    if (this.projectionTools) return this.projectionTools.exportEmbossingForMeshId(meshId);
    return [];
  }

  importEmbossingForMeshId(meshId: string, embossingList: EmbossingExportData[]): void {
    if (this.projectionTools) this.projectionTools.importEmbossingForMeshId(meshId, embossingList);
  }

  exportCurves(): CurveExportData[] {
    if (this.curveTools) return this.curveTools.exportCurves();
    return [];
  }

  importCurves(data: CurveExportData[]): void {
    if (this.curveTools) this.curveTools.importCurves(data);
  }

  exportCameras(): CameraExportData[] {
    if (this.renderTools) return this.renderTools.exportCameras();
    return [];
  }

  importCameras(data: CameraExportData[]): void {
    if (this.renderTools) this.renderTools.importCameras(data);
  }
  //#endregion import / export

  takeSnapshot(): void {
    this.$emit('takeSnapshot', this.view);
  }

  toggleOpacity(): void {
    const selectedMesh = this.modelValue.treeData.find((x) => x.isSelected);

    if (selectedMesh) {
      selectedMesh.opacity = selectedMesh.opacity === 100 ? 0 : 100;
      THREEMaterial.setOpacity(selectedMesh.mesh, selectedMesh.opacity);
    }
  }

  setOpacity(selectedMesh: MeshTreeDataItem, opacity: number): void {
    if (selectedMesh) {
      selectedMesh.opacity = opacity;
      THREEMaterial.setOpacity(selectedMesh.mesh, selectedMesh.opacity);
    }
  }

  async showProductParameterModalDialog(): Promise<void> {
    await this.$emit('showProductParameterModalDialog');
  }

  async showComponentsModalDialog(): Promise<void> {
    await this.$emit('showComponentsModalDialog');
  }

  async download3DFileToObj(): Promise<void> {
    await this.$emit('download3DFileToObj', this.sceneRef);
  }

  closeDeformationEditor(): void {
    this.$emit('closeDeformationEditor');
  }

  get currentPage() {
    return this.route.path.replace(/\//g, ''); // get router slug
  }
}
</script>

<style lang="scss" scoped>
.el-container {
  min-height: 20rem;
  overflow-x: hidden;

  .el-main {
    overflow-y: hidden;
    position: relative;
  }
}

.el-aside {
  background-color: white;
}

.minimize :deep() {
  width: 0;
  color: var(--color-sidebar);
}

.minimize :deep(.expander-side) {
  background-color: white;
}

.active {
  color: var(--el-color-primary);
}

.displayContents {
  display: contents;
  height: 93vh;
}

.fillHorizontal {
  min-height: 20rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.webGlView {
  background: linear-gradient(180deg, #f0f0f0, #d6d6d6);
  z-index: 1;
  // Adding style to support 2 column
  // display: flex;
  // justify-content: center;
  // align-items: center;
  // height: 100vh;
}

// .splitView {
//   display: flex;
//   width: 100%;
//   height: 100%;
// }

// .column {
//   flex: 1;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   border-right: 1px solid #ccc;
// }

.column:last-child {
  border-right: none;
  // Adding style to support 2 column
  // display: flex;
  // justify-content: center;
  // align-items: center;
  // height: 100vh;
}

// .splitView {
//   display: flex;
//   width: 100%;
//   height: 100%;
// }

// .column {
//   flex: 1;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   border-right: 1px solid #ccc;
// }

.column:last-child {
  border-right: none;
}

.fullscreen {
  top: 0;
  left: 0;
  position: absolute;
  width: 100vw;
  height: 100vh;

  .fillHorizontal {
    height: 100vh;
  }
}

.logo {
  position: absolute;
  margin-top: 2rem;
  margin-left: 2rem;
}

.level,
.level-left {
  display: flex;
  align-items: center;
}

.level-right {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.level:not(:last-child) {
  margin-bottom: 0;
}

.level-item:not(:last-child) {
  display: flex;
  align-items: center;
  margin-right: 0.75rem;
  margin-bottom: 0;
}

.hierarchy {
  position: absolute;
  height: 100%;

  .el-scrollbar .el-space {
    padding: 0.5rem 1rem;
    margin-right: unset;
  }

  .el-scrollbar {
    --el-scrollbar-bg-color: var(--el-text-color-primary);
    --el-scrollbar-hover-bg-color: var(--el-text-color-primary);
  }

  .el-space .upload-item {
    width: 6rem;
    height: 6rem;
  }

  .el-space:hover {
    cursor: pointer;
  }
}

.toolbar {
  position: relative;
  padding: 0.4rem 1.8rem;
  align-items: center;
  justify-content: space-between;
  background-color: #efefef;
}

:deep(.el-loading-spinner) {
  position: relative !important;
  justify-self: center !important;
  align-self: center !important;
}

.loading-text {
  position: absolute;
  left: 40px;
  bottom: 65px;
  z-index: 101 !important;
}

.without-footer {
  bottom: 20px;
}
</style>
